import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { activityPageSchema, userPageSchema, type User } from '@bekebi/contracts/api';
import { apiRequest } from '../../api/client';
import { userKeys } from '../users/api';

/**
 * Data access for the Admin Activity area — the existing endpoint only:
 *   GET /api/activity[?limit=&before=]   audit entries, newest first (Admin: every entry)
 * The API pages by entry id: its `nextCursor` is the id to send back as `before`.
 *
 * An entry names its actor only by id. To show who that was, the page reads the existing user
 * list (GET /api/users, `users.manage`); when it cannot, the actor is shown by role only.
 */
export const ACTIVITY_PAGE_SIZE = 50;
export const activityKeys = { all: ['activity'] as const };

export function useActivity() {
  return useInfiniteQuery({
    queryKey: activityKeys.all,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: String(ACTIVITY_PAGE_SIZE) });
      if (pageParam) params.set('before', pageParam);
      return apiRequest(`/activity?${params}`, { schema: activityPageSchema });
    },
    getNextPageParam: (last) => last.nextCursor,
  });
}

/** Every portal user (bounded), to name activity actors. Only with `users.manage`. */
export function useUserDirectory(enabled: boolean) {
  return useQuery({
    queryKey: [...userKeys.all, 'directory'],
    queryFn: async (): Promise<User[]> => {
      const items: User[] = [];
      let cursor: string | null = null;
      for (let page = 0; page < 20; page += 1) {
        const params = new URLSearchParams({ limit: '100' });
        if (cursor) params.set('cursor', cursor);
        const result = await apiRequest(`/users?${params}`, { schema: userPageSchema });
        items.push(...result.items);
        cursor = result.nextCursor;
        if (!cursor) break;
      }
      return items;
    },
    enabled,
  });
}
